import { Container, Graphics, LINE_CAP, LINE_JOIN, Text } from "pixi.js"

import { TextRenderer } from "../text/text_renderer"
import { trackLetters } from "../text/typography"
import { GOLD, INK, RIM, TIMBER_EDGE } from "./build_board"
import { clamp01, easeInCubic, easeOutCubic, lerpColor, span } from "./chapter_reward_motion"
import { ControlsLessonId } from "game-common/models"
import { CONTROLS_LESSON_STEPS, ControlsLessonState, ControlsMoment, ControlsNotePose, ControlsStep, ReadyPress } from "./controls_lesson_state"
import { HUD_PANEL_TEXT_MUTED, UI_THEMES } from "./ui_tokens"

// THE LOOK OF THE CONTROLS LESSON: a legend, not a bubble.
//
// A chat bubble in this game is a timber plate with a tail, over somebody's head, so anything drawn
// that way beside the player reads as the player talking. These notes have neither. The controls
// themselves stand on their own -- the sprint lesson's keycaps, a mouse, a phone's screen with the
// half to touch lit -- beside the player at waist height and a little apart, labelled in small
// tracked capitals over a soft shadow that keeps them legible on grass and trees. The gold spark is
// the clue notes', so they still read as the game teaching.
//
// They stand to the right of the screen, right-aligned in from its edge, so they never crowd the
// middle where the player and whoever is talking to them are. They do not follow the player: each is
// fixed level with where the player stood when its lesson began, and a lesson's next note comes in
// the same place.
//
// No filters and no textures (see the budget hardware target): the shadow is a stack of faint
// ellipses drawn once, the glows are wide low-alpha strokes, and the moving parts are redrawn from
// the lesson's clock only while a note is on screen -- a few seconds on the wreck beach, then the
// first half-minute of a first camp.

const CAP_FILL = UI_THEMES.ember.fill
const CAP_ON = 0x594326
const CAP_RIM = UI_THEMES.ember.border
const UNDER = 0x0b0807
const SHADOW = 0x0c0907
const CRATE = 0x5c4330
const CRATE_ON = 0x7a5a34

// Where a note stands: its right edge this far in from the screen's, level with the player's middle.
const RIGHT_INSET = 40
// Phones get bigger notes for the same reason they get bigger controls.
const TOUCH_SCALE = 1.25
const TICK_ROOM = 12

const label = (text: string): Text =>
    new TextRenderer().fontSize(10).bold().strokeThickness(3).render(trackLetters(text)) as Text
const caption = (text: string): Text =>
    new TextRenderer().fontSize(11).strokeThickness(3).color(HUD_PANEL_TEXT_MUTED).render(text) as Text

// ---- drawing ------------------------------------------------------------------------------------

/** A soft dark pool behind a note: faint ellipses stacked, so it darkens toward the middle. */
const drawScrim = (g: Graphics, cx: number, cy: number, rx: number, ry: number) => {
    const STEPS = 12
    for (let i = 0; i < STEPS; i++) {
        const k = 1 - (i / STEPS) * 0.72
        g.beginFill(SHADOW, 0.042).drawEllipse(cx, cy, rx * k, ry * k).endFill()
    }
}

/** The clue notes' four-point spark. It turns into place as its note arrives. */
const drawSpark = (g: Graphics, cx: number, cy: number, age: number) => {
    const sp = clamp01((age - 60) / 640)
    const rot = (1 - easeOutCubic(sp)) * Math.PI * 0.75, swell = Math.sin(Math.PI * sp)
    const r = 4 * (1 + 0.45 * swell), k = r * 0.3
    if (swell > 0.01) g.lineStyle(0).beginFill(GOLD, 0.22 * swell).drawCircle(cx, cy, r * 2.3).endFill()
    const c = Math.cos(rot), s = Math.sin(rot)
    const points = [[0, -r], [k, -k], [r, 0], [k, k], [0, r], [-k, k], [-r, 0], [-k, -k]]
        .flatMap(([x, y]) => [cx + x * c - y * s, cy + x * s + y * c])
    g.lineStyle({ width: 2, color: TIMBER_EDGE, alpha: 0.92, join: LINE_JOIN.ROUND })
        .beginFill(GOLD).drawPolygon(points).endFill().lineStyle(0)
}

/** A gold tick that stamps in oversized and settles. */
const drawTick = (g: Graphics, x: number, y: number, p: number) => {
    if (p <= 0) return
    const s = 1 + 0.9 * (1 - easeOutCubic(p)), a = clamp01(p * 1.6)
    const pts = [[-3.4, 0], [-1, 2.6], [3.6, -3]].map(([px, py]) => [x + px * s, y + py * s])
    const stroke = (width: number, color: number, alpha: number) => {
        g.lineStyle({ width, color, alpha, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
        g.moveTo(pts[0][0], pts[0][1]).lineTo(pts[1][0], pts[1][1]).lineTo(pts[2][0], pts[2][1])
    }
    stroke(3.6, TIMBER_EDGE, 0.92 * a)
    stroke(1.7, GOLD, a)
    g.lineStyle(0)
}

const KEY = 18, KEY_GAP = 3
const CLUSTER_W = KEY * 3 + KEY_GAP * 2

/** One keycap, pressed `p` (0..1) of the way down. Returns how far its face sank. */
const drawKey = (g: Graphics, x: number, y: number, p: number, appear: number, arrow?: number): number => {
    if (appear <= 0) return 0
    y += (1 - appear) * 3
    const a = appear, dy = 1.5 * p
    g.lineStyle(0)
    g.beginFill(SHADOW, 0.55 * a).drawRoundedRect(x - 1, y - 1, KEY + 2, KEY + 4, 4).endFill()
    g.beginFill(UNDER, a).drawRoundedRect(x, y + 2, KEY, KEY, 3).endFill()
    if (p > 0.02) g.lineStyle(3, GOLD, 0.26 * p * a).drawRoundedRect(x - 1.5, y + dy - 1.5, KEY + 3, KEY + 3, 4.5)
    g.lineStyle(1, lerpColor(CAP_RIM, GOLD, p), a).beginFill(lerpColor(CAP_FILL, CAP_ON, p), a)
        .drawRoundedRect(x, y + dy, KEY, KEY, 3).endFill()
    g.lineStyle(1, INK, (0.12 + 0.22 * p) * a).moveTo(x + 3.5, y + dy + 1.5).lineTo(x + KEY - 3.5, y + dy + 1.5)
    g.lineStyle(0)
    if (arrow !== undefined) {
        const cx = x + KEY / 2, cy = y + dy + KEY / 2 + 0.5, c = Math.cos(arrow), s = Math.sin(arrow)
        const points = [[0, -4], [4.3, 2.7], [-4.3, 2.7]].flatMap(([px, py]) => [cx + px * c - py * s, cy + px * s + py * c])
        g.beginFill(lerpColor(HUD_PANEL_TEXT_MUTED, INK, Math.max(p, 0.35)), a).drawPolygon(points).endFill()
    }
    return dy
}

/** A mouse, its left and right buttons lit `pl` and `pr` of the way. */
const drawMouse = (g: Graphics, x: number, y: number, w: number, h: number, pl: number, pr: number) => {
    const r = w / 2, cx = x + r, split = y + h * 0.42
    g.lineStyle(0)
    g.beginFill(SHADOW, 0.55).drawRoundedRect(x - 1, y - 1, w + 2, h + 3, r + 1).endFill()
    g.beginFill(CAP_FILL).drawRoundedRect(x, y, w, h, r).endFill()
    const button = (side: -1 | 1, p: number) => {
        if (p <= 0.01) return
        g.beginFill(GOLD, 0.85 * p)
        g.moveTo(cx, split).lineTo(cx, y)
        g.arc(cx, y + r, r, -Math.PI / 2, side < 0 ? -Math.PI : 0, side < 0)
        g.lineTo(cx + side * r, split).lineTo(cx, split)
        g.endFill()
    }
    button(-1, pl)
    button(1, pr)
    const lit = Math.max(pl, pr)
    if (lit > 0.02) g.lineStyle(3, GOLD, 0.22 * lit).drawRoundedRect(x - 1.5, y - 1.5, w + 3, h + 3, r + 1.5)
    g.lineStyle(3, TIMBER_EDGE, 1).drawRoundedRect(x, y, w, h, r)
    g.lineStyle(1.1, lerpColor(CAP_RIM, GOLD, lit * 0.7), 1).drawRoundedRect(x, y, w, h, r)
    g.moveTo(x + 1, split).lineTo(x + w - 1, split).moveTo(cx, y + 1).lineTo(cx, split)
    g.lineStyle(0).beginFill(lerpColor(RIM, INK, lit * 0.5)).drawRoundedRect(cx - 1.5, y + 6, 3, 7, 1.5).endFill()
}

const SCREEN_W = 52, SCREEN_H = 29

/** A phone held sideways, the half to touch lit, and a thumb that presses and drags across it. */
const drawScreen = (g: Graphics, x: number, y: number, side: "left" | "right", lit: number, age: number, animate: boolean) => {
    const w = SCREEN_W, h = SCREEN_H
    g.lineStyle(0)
    g.beginFill(SHADOW, 0.55).drawRoundedRect(x - 1, y - 1, w + 2, h + 3, 6).endFill()
    g.beginFill(CAP_FILL).drawRoundedRect(x, y, w, h, 5).endFill()
    g.beginFill(GOLD, 0.2 + 0.45 * lit).drawRoundedRect(side === "left" ? x + 1.5 : x + w / 2, y + 1.5, w / 2 - 1.5, h - 3, 3.5).endFill()
    if (lit > 0.02) g.lineStyle(3, GOLD, 0.22 * lit).drawRoundedRect(x - 1.5, y - 1.5, w + 3, h + 3, 6.5)
    g.lineStyle(3, TIMBER_EDGE, 1).drawRoundedRect(x, y, w, h, 5)
    g.lineStyle(1.2, lerpColor(CAP_RIM, GOLD, 0.25 + 0.6 * lit), 1).drawRoundedRect(x, y, w, h, 5)
    g.lineStyle(1, HUD_PANEL_TEXT_MUTED, 0.4)
    for (let dy = 4; dy < h - 5; dy += 4) g.moveTo(x + w / 2, y + dy).lineTo(x + w / 2, y + dy + 2)
    let o = 1, a = 1
    if (animate) {
        const k = (Math.max(0, age) % 1800) / 1800
        o = k < 0.2 ? 0 : k < 0.55 ? easeOutCubic((k - 0.2) / 0.35) : 1
        a = k < 0.1 ? k / 0.1 : k > 0.8 ? 1 - (k - 0.8) / 0.2 : 1
    }
    const cx = side === "left" ? x + 9 : x + w / 2 + 8, cy = y + h / 2 + 4
    const tx = cx + (side === "left" ? 12 : 10) * o, ty = cy + (side === "left" ? 0 : -6.5) * o
    g.lineStyle({ width: 1.4, color: INK, alpha: 0.4 * a, cap: LINE_CAP.ROUND }).moveTo(cx, cy).lineTo(tx, ty)
    g.lineStyle(1.2, INK, 0.3 * a).drawCircle(cx, cy, 5)
    g.lineStyle(0).beginFill(INK, 0.95 * a).drawCircle(tx, ty, 3.6).endFill()
}

/** A crate, and a finger held down on it until the ring round it closes. */
const drawHold = (g: Graphics, x: number, y: number, lit: number, age: number, animate: boolean) => {
    const cx = x + SCREEN_W / 2 - 3, cy = y + SCREEN_H / 2 + 3
    g.lineStyle(0).beginFill(SHADOW, 0.55).drawRoundedRect(cx - 11, cy - 7.5, 22, 17, 2.5).endFill()
    g.lineStyle(2.6, TIMBER_EDGE, 1).beginFill(lerpColor(CRATE, CRATE_ON, lit)).drawRoundedRect(cx - 10, cy - 6.5, 20, 14, 2).endFill()
    g.lineStyle(1, lerpColor(CAP_RIM, GOLD, 0.2 + 0.7 * lit), 1).drawRoundedRect(cx - 10, cy - 6.5, 20, 14, 2)
    g.lineStyle(1, TIMBER_EDGE, 0.8).moveTo(cx - 9, cy - 1.5).lineTo(cx + 9, cy - 1.5)
    g.lineStyle(0).beginFill(INK, 0.7).drawRect(cx - 1.3, cy - 2.5, 2.6, 3).endFill()
    const tx = cx + 11, ty = cy - 8
    let dot = 0.9, fill = 1, ring = 0.5
    if (animate) {
        const k = (Math.max(0, age) % 1800) / 1800
        dot = k < 0.08 ? 0 : k < 0.16 ? (k - 0.08) / 0.08 : k < 0.8 ? 1 : k < 0.9 ? 1 - (k - 0.8) / 0.1 : 0
        fill = easeOutCubic(span(k, 0.18, 0.7))
        ring = dot * 0.9
    }
    if (fill > 0.01 && ring > 0.01) {
        g.lineStyle({ width: 1.8, color: GOLD, alpha: ring, cap: LINE_CAP.ROUND })
        g.moveTo(tx, ty - 7.5).arc(tx, ty, 7.5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * fill)
    }
    if (dot > 0.01) g.lineStyle(0).beginFill(INK, dot).drawCircle(tx, ty, 3.6).endFill()
    g.lineStyle(0)
}

/** A phone held sideways, its bottom-left corner -- where the ready bar sits -- lit, and a thumb tapping a slot. */
const drawTrayTap = (g: Graphics, x: number, y: number, lit: number, age: number, animate: boolean) => {
    const w = SCREEN_W, h = SCREEN_H
    g.lineStyle(0)
    g.beginFill(SHADOW, 0.55).drawRoundedRect(x - 1, y - 1, w + 2, h + 3, 6).endFill()
    g.beginFill(CAP_FILL).drawRoundedRect(x, y, w, h, 5).endFill()
    // The tray: three small wells in the corner, on a lit strip.
    const tx = x + 4.5, ty = y + h - 10.5
    g.beginFill(GOLD, 0.2 + 0.45 * lit).drawRoundedRect(tx - 2, ty - 2, 3 * 6 + 2 * 2 + 4, 10, 2.5).endFill()
    for (let i = 0; i < 3; i++) g.beginFill(UNDER, 0.85).drawRoundedRect(tx + i * 8, ty, 6, 6, 1.5).endFill()
    if (lit > 0.02) g.lineStyle(3, GOLD, 0.22 * lit).drawRoundedRect(x - 1.5, y - 1.5, w + 3, h + 3, 6.5)
    g.lineStyle(3, TIMBER_EDGE, 1).drawRoundedRect(x, y, w, h, 5)
    g.lineStyle(1.2, lerpColor(CAP_RIM, GOLD, 0.25 + 0.6 * lit), 1).drawRoundedRect(x, y, w, h, 5)
    // The thumb comes down on the first slot, presses (its ring opens), and lifts.
    let a = 0.9, ring = 0
    if (animate) {
        const k = (Math.max(0, age) % 1800) / 1800
        a = k < 0.12 ? 0 : k < 0.24 ? (k - 0.12) / 0.12 : k < 0.7 ? 1 : k < 0.82 ? 1 - (k - 0.7) / 0.12 : 0
        ring = span(k, 0.3, 0.6)
    }
    const cx = tx + 3, cy = ty + 3
    if (ring > 0 && ring < 1) g.lineStyle(1.2, INK, 0.45 * a * (1 - ring)).drawCircle(cx, cy, 4 + 5 * easeOutCubic(ring))
    g.lineStyle(0).beginFill(INK, 0.95 * a).drawCircle(cx, cy, 3.6).endFill()
}

/** Where a thumb rests: a faint stick that shows the drag it is waiting for. */
const GHOST_R = 44
const drawGhostStick = (g: Graphics, cx: number, cy: number, alpha: number, age: number, dx: number, dy: number) => {
    if (alpha <= 0.01) return
    const k = (Math.max(0, age) % 2400) / 2400
    const out = k < 0.15 ? 0 : k < 0.5 ? easeOutCubic((k - 0.15) / 0.35) : k < 0.75 ? 1 : 1 - easeOutCubic((k - 0.75) / 0.25)
    g.lineStyle(0).beginFill(SHADOW, 0.2 * alpha).drawCircle(cx, cy, GHOST_R + 2).endFill()
    const DASHES = 18
    for (let i = 0; i < DASHES; i++) {
        const a0 = (i / DASHES) * Math.PI * 2, a1 = a0 + (Math.PI * 2 / DASHES) * 0.55
        g.lineStyle(1.5, INK, 0.32 * alpha).moveTo(cx + Math.cos(a0) * GHOST_R, cy + Math.sin(a0) * GHOST_R)
            .arc(cx, cy, GHOST_R, a0, a1)
    }
    g.lineStyle(1.2, INK, 0.45 * alpha).beginFill(INK, 0.2 * alpha)
        .drawCircle(cx + dx * 22 * out, cy + dy * 22 * out, 17).endFill().lineStyle(0)
}

const pulseAt = (t: number, period = 2800, length = 560) => {
    if (t < 0) return 0
    const k = t % period
    return k < length ? Math.sin((Math.PI * k) / length) : 0
}
const appearAt = (age: number, delay: number) => easeOutCubic(clamp01((age - delay) / 300))

// ---- the notes ----------------------------------------------------------------------------------

/** What the notes are drawn from, besides their pose. All 0..1 eased values except the used times. */
interface NoteInput {
    keyLit: (key: string) => number
    attackLit: number
    interactLit: number
    moveLit: number
    moved: boolean
    attackUsedAt?: number
    interactUsedAt?: number
    /** The ready note: how lit the slot last pressed is, and which slot that was. */
    readyLit: number
    readySlot?: number
    /** The ready note: a press took something out (done), or put the item in hand away (asking for it back). */
    readyUsedAt?: number
    stowedAt?: number
    stowedSlot?: number
    now: number
}

abstract class Note extends Container {
    protected readonly scrim = new Graphics()
    protected readonly g = new Graphics()
    width_ = 0

    constructor() {
        super()
        this.addChild(this.scrim, this.g)
    }

    abstract draw: (pose: ControlsNotePose, input: NoteInput) => void

    protected place = (text: Text, x: number, y: number, ax: number) => {
        text.anchor.set(ax, 0.5)
        text.position.set(Math.round(x), Math.round(y))
        this.addChild(text)
        return text
    }
}

/** The movement keys, both sets, over MOVE. */
class MoveKeysNote extends Note {
    private readonly letters: Text[]
    private readonly or: Text
    private readonly title: Text
    private readonly secondX: number
    private static readonly KEYS: [string, string | number, number, number][][] = [
        [["w", "W", 1, 0], ["a", "A", 0, 1], ["s", "S", 1, 1], ["d", "D", 2, 1]],
        [["ArrowUp", 0, 1, 0], ["ArrowLeft", -Math.PI / 2, 0, 1], ["ArrowDown", Math.PI, 1, 1], ["ArrowRight", Math.PI / 2, 2, 1]],
    ]
    private static readonly TOP = -31

    constructor() {
        super()
        this.or = caption("or")
        const orGap = 12
        this.secondX = CLUSTER_W + orGap * 2 + this.or.width
        this.width_ = this.secondX + CLUSTER_W
        drawScrim(this.scrim, this.width_ / 2, -3, this.width_ / 2 + 40, 50)
        this.place(this.or, CLUSTER_W + orGap, MoveKeysNote.TOP + KEY + KEY_GAP + KEY / 2 + 1, 0)
        this.letters = MoveKeysNote.KEYS[0].map(([, glyph]) => {
            const text = new TextRenderer().fontSize(11).bold().strokeThickness(0).render(glyph as string) as Text
            text.anchor.set(0.5)
            this.addChild(text)
            return text
        })
        this.title = this.place(label("MOVE"), this.width_ / 2 + 7, 21, 0.5)
    }

    draw = (pose: ControlsNotePose, input: NoteInput) => {
        const g = this.g.clear()
        let i = 0
        MoveKeysNote.KEYS.forEach((group, gi) => group.forEach(([key, glyph, col, row]) => {
            const x = (gi ? this.secondX : 0) + col * (KEY + KEY_GAP), y = MoveKeysNote.TOP + row * (KEY + KEY_GAP)
            const p = Math.max(input.keyLit(key), pose.flash * 0.5), appear = appearAt(pose.age, 100 + i * 40)
            const sank = drawKey(g, x, y, p, appear, typeof glyph === "number" ? glyph : undefined)
            if (typeof glyph === "string") {
                const letter = this.letters[i]
                letter.position.set(x + KEY / 2, y + (1 - appear) * 3 + sank + KEY / 2 + 0.5)
                letter.alpha = appear
                letter.tint = lerpColor(HUD_PANEL_TEXT_MUTED, INK, Math.max(p, 0.35))
            }
            i++
        }))
        this.or.alpha = appearAt(pose.age, 300)
        this.title.alpha = appearAt(pose.age, 380)
        this.title.tint = lerpColor(INK, GOLD, pose.flash)
        drawSpark(g, this.title.x - this.title.width / 2 - 9, this.title.y, pose.age)
    }
}

/** One mouse button's action: ATTACK beside the left button, INTERACT beside the right. */
class MouseNote extends Note {
    private readonly verb: Text
    private readonly how: Text
    private readonly mouseX: number
    private readonly textX: number
    private static readonly W = 26
    private static readonly H = 38

    constructor(private readonly button: "left" | "right") {
        super()
        const gap = 14
        this.verb = label(button === "left" ? "ATTACK" : "INTERACT")
        this.how = caption(button === "left" ? "left click" : "right click")
        const textW = Math.max(this.verb.width, this.how.width + TICK_ROOM)
        // The words sit on the side of the button they name, the spark ahead of them.
        if (button === "left") {
            this.textX = 13 + textW
            this.mouseX = this.textX + gap
            this.width_ = this.mouseX + MouseNote.W
            this.place(this.verb, this.textX, -7, 1)
            this.place(this.how, this.textX, 9, 1)
        } else {
            this.mouseX = 0
            this.textX = MouseNote.W + gap + 13
            this.width_ = this.textX + textW
            this.place(this.verb, this.textX, -7, 0)
            this.place(this.how, this.textX, 9, 0)
        }
        drawScrim(this.scrim, this.width_ / 2, -3, this.width_ / 2 + 36, 46)
    }

    draw = (pose: ControlsNotePose, input: NoteInput) => {
        const g = this.g.clear()
        const usedAt = this.button === "left" ? input.attackUsedAt : input.interactUsedAt
        const lit = Math.max(
            this.button === "left" ? input.attackLit : input.interactLit,
            usedAt === undefined ? 0.7 * pulseAt(pose.age - 900) : 0,
            pose.flash * 0.4,
        )
        const words = appearAt(pose.age, 160)
        this.verb.alpha = this.how.alpha = words
        this.verb.tint = lerpColor(INK, GOLD, Math.max(lit * 0.6, pose.flash))
        const mouseAppear = appearAt(pose.age, 80)
        if (mouseAppear > 0) {
            const l = this.button === "left" ? lit * mouseAppear : 0, r = this.button === "right" ? lit * mouseAppear : 0
            drawMouse(g, this.mouseX, -22 + (1 - mouseAppear) * 3, MouseNote.W, MouseNote.H, l, r)
        }
        const nameLeft = this.button === "left" ? this.verb.x - this.verb.width : this.verb.x
        drawSpark(g, nameLeft - 9, this.verb.y, pose.age)
        if (usedAt !== undefined) {
            const tickX = this.button === "left" ? this.how.x - this.how.width - 8 : this.how.x + this.how.width + 8
            drawTick(g, tickX, this.how.y, clamp01((input.now - usedAt) / 320) * words)
        }
    }
}

/** A phone's lesson: columns of [gesture over label over caption]. */
interface TouchColumn {
    glyph: "left" | "right" | "hold"
    label: Text
    caption: Text
    x: number
    w: number
}

class TouchNote extends Note {
    private readonly columns: TouchColumn[]
    private static readonly GLYPH_Y = -36

    constructor(columns: { glyph: TouchColumn["glyph"]; label: string; caption: string }[]) {
        super()
        let x = 0
        this.columns = columns.map((spec, i) => {
            const name = label(spec.label), words = caption(spec.caption)
            // An action's caption takes a tick once it is done: room for it either side keeps the words centred.
            const w = Math.max(SCREEN_W, name.width + (i === 0 ? 13 : 0), words.width + (spec.glyph === "left" ? 0 : TICK_ROOM * 2))
            const column = { glyph: spec.glyph, label: name, caption: words, x, w }
            x += w + 24
            return column
        })
        this.width_ = x - 24
        drawScrim(this.scrim, this.width_ / 2, -4, this.width_ / 2 + 38, 52)
        this.columns.forEach((column, i) => {
            const cx = column.x + column.w / 2
            this.place(column.label, cx + (i === 0 ? 7 : 0), 8, 0.5)
            this.place(column.caption, cx, 22, 0.5)
        })
    }

    draw = (pose: ControlsNotePose, input: NoteInput) => {
        const g = this.g.clear()
        this.columns.forEach((column, i) => {
            const cx = column.x + column.w / 2
            const usedAt = column.glyph === "right" ? input.attackUsedAt : column.glyph === "hold" ? input.interactUsedAt : undefined
            const lit = Math.max(
                column.glyph === "left" ? input.moveLit : column.glyph === "right" ? input.attackLit : input.interactLit,
                pose.flash * 0.5,
            )
            const animate = usedAt === undefined && !(column.glyph === "left" && input.moved)
            const glyphAppear = appearAt(pose.age, 100 + i * 120), textAppear = appearAt(pose.age, 220 + i * 120)
            const gx = cx - SCREEN_W / 2, gy = TouchNote.GLYPH_Y + (1 - glyphAppear) * 3
            if (glyphAppear > 0) {
                if (column.glyph === "hold") drawHold(g, gx, gy, lit, pose.age - i * 900, animate)
                else drawScreen(g, gx, gy, column.glyph, lit, pose.age - i * 900, animate)
            }
            column.label.alpha = column.caption.alpha = textAppear
            column.label.tint = lerpColor(INK, GOLD, Math.max(lit * 0.6, pose.flash))
            if (i === 0) drawSpark(g, column.label.x - column.label.width / 2 - 9, column.label.y, pose.age)
            if (usedAt !== undefined) {
                drawTick(g, cx + column.caption.width / 2 + 8, column.caption.y, clamp01((input.now - usedAt) / 320) * textAppear)
            }
        })
    }
}

// The bar answers to 1 to 6, but four keys are enough to say that each slot has its number, and a
// new player's bar holds one or two things.
const READY_KEYS = 4
const READY_ROW_W = READY_KEYS * KEY + (READY_KEYS - 1) * KEY_GAP
// How long the ready note takes to turn from its row of keys to the one key it asks for.
const STOW_TURN_MS = 260

/**
 * The ready bar's keys, 1 to 4, over READY BAR -- on a phone, a thumb tapping the tray's corner. A
 * press that puts the item in hand away turns the row into the one key (the one tap) that brings it
 * back: the note answers what the player did instead of carrying on as if they had done it right.
 */
class ReadyNote extends Note {
    private readonly digits: Text[] = []
    private readonly back?: Text
    private readonly firstName: Text
    private readonly firstHow: Text
    private readonly backName: Text
    private readonly backHow: Text
    private shownSlot = 1
    private static readonly ROW_Y = -27
    private static readonly GLYPH_Y = -36

    constructor(private readonly touch: boolean) {
        super()
        this.firstName = label("READY BAR")
        this.firstHow = caption(touch ? "tap a slot to hold or put away" : "press a number to hold or put away")
        this.backName = label("PUT AWAY")
        this.backHow = caption(touch ? "tap it again to take it out" : "press 1 to take it back out")
        // The captions take a tick once the lesson is done: room for it either side keeps them centred.
        const textW = Math.max(this.firstHow.width, this.backHow.width) + TICK_ROOM * 2
        this.width_ = Math.max(touch ? SCREEN_W : READY_ROW_W, textW, this.firstName.width + 13)
        drawScrim(this.scrim, this.width_ / 2, -4, this.width_ / 2 + 38, 52)
        const cx = this.width_ / 2
        this.place(this.firstName, cx + 7, 8, 0.5)
        this.place(this.firstHow, cx, 22, 0.5)
        this.place(this.backName, cx + 7, 8, 0.5)
        this.place(this.backHow, cx, 22, 0.5)
        this.backName.alpha = this.backHow.alpha = 0
        if (touch) return
        const digit = (glyph: string) => {
            const text = new TextRenderer().fontSize(11).bold().strokeThickness(0).render(glyph) as Text
            text.anchor.set(0.5)
            text.alpha = 0
            this.addChild(text)
            return text
        }
        for (let i = 1; i <= READY_KEYS; i++) this.digits.push(digit(`${i}`))
        this.back = digit("1")
    }

    draw = (pose: ControlsNotePose, input: NoteInput) => {
        const g = this.g.clear()
        if (input.stowedSlot !== undefined && input.stowedSlot !== this.shownSlot) {
            this.shownSlot = input.stowedSlot
            if (this.back) this.back.text = `${this.shownSlot}`
            if (!this.touch) this.backHow.text = `press ${this.shownSlot} to take it back out`
        }
        const turn = input.stowedAt === undefined ? 0 : easeOutCubic(clamp01((input.now - input.stowedAt) / STOW_TURN_MS))
        const words = appearAt(pose.age, 160)
        this.firstName.alpha = this.firstHow.alpha = words * (1 - turn)
        this.backName.alpha = this.backHow.alpha = words * turn
        this.firstName.tint = this.backName.tint = lerpColor(INK, GOLD, Math.max(input.readyLit * 0.6, pose.flash))
        const done = input.readyUsedAt !== undefined

        if (this.touch) {
            const appear = appearAt(pose.age, 100)
            if (appear > 0) {
                drawTrayTap(g, this.width_ / 2 - SCREEN_W / 2, ReadyNote.GLYPH_Y + (1 - appear) * 3,
                    Math.max(input.readyLit, pose.flash * 0.5), pose.age, !done)
            }
        } else {
            const rowX = this.width_ / 2 - READY_ROW_W / 2
            this.digits.forEach((digit, i) => {
                const x = rowX + i * (KEY + KEY_GAP), y = ReadyNote.ROW_Y
                // Until something is pressed a light runs along the row: any of these will do.
                const p = Math.max(input.readySlot === i + 1 ? input.readyLit : 0,
                    done ? 0 : 0.55 * pulseAt(pose.age - 900 - i * 90), pose.flash * 0.5)
                const appear = appearAt(pose.age, 100 + i * 40) * (1 - turn)
                const sank = drawKey(g, x, y, p, appear)
                digit.position.set(x + KEY / 2, y + (1 - appear) * 3 + sank + KEY / 2 + 0.5)
                digit.alpha = appear
                digit.tint = lerpColor(HUD_PANEL_TEXT_MUTED, INK, Math.max(p, 0.35))
            })
            if (this.back) {
                const x = this.width_ / 2 - KEY / 2, y = ReadyNote.ROW_Y
                const p = Math.max(input.readySlot === this.shownSlot ? input.readyLit : 0,
                    done || input.stowedAt === undefined ? 0 : 0.7 * pulseAt(input.now - input.stowedAt - 600, 1800),
                    pose.flash * 0.5)
                const sank = drawKey(g, x, y, p, turn)
                this.back.position.set(x + KEY / 2, y + (1 - turn) * 3 + sank + KEY / 2 + 0.5)
                this.back.alpha = turn
                this.back.tint = lerpColor(HUD_PANEL_TEXT_MUTED, INK, Math.max(p, 0.35))
            }
        }

        const name = turn < 0.5 ? this.firstName : this.backName
        drawSpark(g, name.x - name.width / 2 - 9, name.y, pose.age)
        if (done) {
            const how = turn < 0.5 ? this.firstHow : this.backHow
            drawTick(g, how.x + how.width / 2 + 8, how.y, clamp01((input.now - input.readyUsedAt!) / 320) * words)
        }
    }
}

// ---- the lesson ---------------------------------------------------------------------------------

/**
 * What GuiManager hands the lesson every frame. The two actions are NOT in it: they arrive as events
 * (noteUsed), from what the player's client sent, so a session replay -- which has what was sent and
 * nothing else -- runs the lesson exactly as the player saw it.
 */
export interface ControlsLessonInput extends Omit<ControlsMoment, "attacking" | "interacting" | "readyPress"> {
    /** Is this key held? Keyboard names as the Keyboard handler keeps them ("w", "ArrowUp"). */
    keyHeld: (key: string) => boolean
    /** The player's centre in stage space: the notes stand level with it. */
    playerAt: () => { x: number; y: number } | null
    /** The visible stage. */
    bounds: { left: number; top: number; right: number; bottom: number }
}

const LIT_UP_MS = 40
const LIT_DOWN_MS = 180
const ease = (value: number, on: boolean, deltaMs: number) =>
    value + ((on ? 1 : 0) - value) * (1 - Math.exp(-deltaMs / (on ? LIT_UP_MS : LIT_DOWN_MS)))

const buildNote = (step: ControlsStep, touch: boolean): Note => {
    if (step === "move") return touch ? new TouchNote([{ glyph: "left", label: "MOVE", caption: "drag on the left side" }]) : new MoveKeysNote()
    if (step === "attack") return touch ? new TouchNote([{ glyph: "right", label: "ATTACK", caption: "drag on the right side" }]) : new MouseNote("left")
    if (step === "ready") return new ReadyNote(touch)
    return touch ? new TouchNote([{ glyph: "hold", label: "INTERACT", caption: "hold on a thing" }]) : new MouseNote("right")
}

/**
 * The chapter-one controls lessons on screen. GuiManager hands it the server's requests, resets it
 * with the scene and feeds it, every frame, the player's input and what covers the world; it decides
 * the rest (see ControlsLessonState for the rules).
 *
 * The notes stand to the right of the screen, right-aligned a comfortable way in from its edge, so the
 * middle -- the player, whoever they are talking to, the bubbles -- stays clear. Level with the
 * player's middle as they stood when the lesson began, and still from then on.
 */
export class ControlsLesson extends Container {
    readonly state = new ControlsLessonState()
    private readonly ghosts = new Graphics()
    private notes = new Map<ControlsStep, Note>()
    private builtFor?: ControlsLessonId
    private touch = false
    private anchorY?: number
    private boundsKey?: string
    private keyLit = new Map<string, number>()
    private lit = { move: 0, attack: 0, interact: 0, ready: 0 }
    private pending: { attack: boolean; interact: boolean; ready?: ReadyPress } = { attack: false, interact: false }
    // The ready-bar slot the player last pressed, and the one they last put away.
    private pressedSlot?: number
    private stowedSlot?: number

    constructor() {
        super()
        this.visible = false
        this.addChild(this.ghosts)
    }

    /** The server asked for a lesson, for the device it is being taught on. */
    request = (lesson: ControlsLessonId, touch: boolean) => {
        this.touch = touch
        this.state.request(lesson)
    }

    /** The server wants no lesson now: whatever is up fades out. */
    dismiss = () => this.state.dismiss()

    reset = () => {
        this.state.reset()
        this.clearNotes()
        this.ghosts.clear()
        this.visible = false
    }

    isActive = () => this.state.isActive()

    /** The lessons the player has seen through to the end since this was last asked. */
    takeFinished = () => this.state.takeFinished()

    /** The player swung, or interacted with something. Counted on the next frame. */
    noteUsed = (action: "attack" | "interact") => {
        if (this.state.isActive()) this.pending[action] = true
    }

    /** The player pressed a ready-bar slot (by key or by tap), and it took that item out or put it away. */
    noteReadyPress = (press: ReadyPress, slot?: number) => {
        if (!this.state.isActive()) return
        this.pending.ready = press
        this.pressedSlot = slot
        if (press === "stow" && slot !== undefined) this.stowedSlot = slot
    }

    animate = (deltaMs: number, input: ControlsLessonInput) => {
        const { attack, interact, ready } = this.pending
        this.pending = { attack: false, interact: false }
        this.state.update(deltaMs, { ...input, attacking: attack, interacting: interact, readyPress: ready })
        const lesson = this.state.current()
        if (lesson !== this.builtFor) this.build(lesson)
        this.ghosts.clear()
        const poses = (lesson ? CONTROLS_LESSON_STEPS[lesson] : []).map(step => [step, this.state.pose(step)] as const)
        if (!lesson || poses.every(([, pose]) => !pose)) {
            this.visible = false
            return
        }
        const { bounds } = input
        const boundsKey = `${bounds.left},${bounds.top},${bounds.right},${bounds.bottom}`
        if (this.anchorY === undefined || boundsKey !== this.boundsKey) {
            const at = input.playerAt()
            if (at) {
                this.anchorY = at.y
                this.boundsKey = boundsKey
            }
        }
        if (this.anchorY === undefined) {
            this.visible = false
            return
        }
        this.visible = true

        for (const key of ["w", "a", "s", "d", "ArrowUp", "ArrowLeft", "ArrowDown", "ArrowRight"]) {
            this.keyLit.set(key, ease(this.keyLit.get(key) || 0, input.keyHeld(key), deltaMs))
        }
        this.lit = {
            move: ease(this.lit.move, input.moving, deltaMs),
            // Each use flashes its control, then lets it cool.
            attack: attack ? 1 : ease(this.lit.attack, false, deltaMs),
            interact: interact ? 1 : ease(this.lit.interact, false, deltaMs),
            ready: ready ? 1 : ease(this.lit.ready, false, deltaMs),
        }
        const noteInput: NoteInput = {
            keyLit: key => this.keyLit.get(key) || 0,
            attackLit: this.lit.attack,
            interactLit: this.lit.interact,
            moveLit: this.lit.move,
            moved: this.state.firstStep() !== undefined,
            attackUsedAt: this.state.usedAt("attack"),
            interactUsedAt: this.state.usedAt("interact"),
            readyLit: this.lit.ready,
            readySlot: this.pressedSlot,
            readyUsedAt: this.state.usedAt("ready"),
            stowedAt: this.state.stowedAt(),
            stowedSlot: this.stowedSlot,
            now: this.state.now(),
        }
        for (const [step, pose] of poses) this.poseNote(this.notes.get(step)!, pose, noteInput, bounds)

        if (this.touch) {
            // Where a thumb goes, for the two steps a stick does: the ghost gives way once it has.
            const w = bounds.right - bounds.left, h = bounds.bottom - bounds.top
            const gone = (at: number | undefined) => at === undefined ? 1 : 1 - easeInCubic(clamp01((this.state.now() - at) / 350))
            for (const [step, pose] of poses) {
                if (!pose) continue
                if (step === "move") {
                    drawGhostStick(this.ghosts, bounds.left + w * 0.17, bounds.top + h * 0.74,
                        0.9 * pose.alpha * gone(this.state.firstStep()), pose.age, 1, 0)
                } else if (step === "attack") {
                    drawGhostStick(this.ghosts, bounds.left + w * 0.83, bounds.top + h * 0.74,
                        0.9 * pose.alpha * gone(this.state.usedAt("attack")), pose.age, 0.85, -0.5)
                }
            }
        }
    }

    private build = (lesson: ControlsLessonId | undefined) => {
        this.clearNotes()
        this.builtFor = lesson
        if (!lesson) return
        for (const step of CONTROLS_LESSON_STEPS[lesson]) {
            const note = buildNote(step, this.touch)
            note.visible = false
            this.notes.set(step, note)
            this.addChild(note)
        }
    }

    private clearNotes = () => {
        this.notes.forEach(note => note.destroy({ children: true }))
        this.notes.clear()
        this.builtFor = undefined
        this.anchorY = this.boundsKey = undefined
        this.keyLit.clear()
        this.lit = { move: 0, attack: 0, interact: 0, ready: 0 }
        this.pressedSlot = this.stowedSlot = undefined
    }

    private poseNote = (note: Note, pose: ControlsNotePose | null, input: NoteInput, bounds: ControlsLessonInput["bounds"]) => {
        note.visible = !!pose && pose.alpha > 0.002
        if (!pose || !note.visible || this.anchorY === undefined) return
        const fit = this.touch ? TOUCH_SCALE : 1
        const scale = fit * pose.scale
        note.position.set(bounds.right - RIGHT_INSET * fit - note.width_ * scale, this.anchorY + pose.rise)
        note.scale.set(scale)
        note.alpha = pose.alpha
        note.draw(pose, input)
    }
}
